import React, { useState } from "react";
import Img from "gatsby-image";
import styled from "@emotion/styled";
import { css } from "@emotion/core";
import { graphql, Link, useStaticQuery } from "gatsby";
import posed, { Transition } from "react-pose";
import Container from "@src/components/Container";
import { Box, Flex, FlexProps } from "@src/components/Boxes";
import Button from "@src/components/Button";
import BurgerButton from "@src/components/BurgerButton";
import { mediaQueries } from "@src/styles/theme";
import { FaSearch } from "react-icons/fa";
import BlogSearchForm from "@src/components/blog/BlogSearchForm";

const bottomBorder = css`
  border-bottom: 1px solid #eee;
`;

const Nav = styled.nav`
  position: fixed;
  background-color: white;
  right: 0;
  left: 0;
  z-index: 1030;
  flex: 0 0 auto;
`;

const SearchIcon = styled.span`
  color: #777;
  font-size: 18px;
  font-weight: 200;
  cursor: pointer;
  margin: 6px 20px 0;
  display: none;

  &:hover {
    color: #00bcf2;
    text-decoration: none;
  }

  ${mediaQueries[1]} {
    display: block;
  }
`;

const Logo = styled(Img)`
  width: 60px;
`;

const SiteName = styled.span`
  color: #222;
  font-size: 22px;
  letter-spacing: -0.02em;
  font-weight: 700;
  position: relative;
  top: 2px;
  margin-left: 4px;

  ${mediaQueries[1]} {
    font-size: 31px;
  }
`;

type NavLinkProps = FlexProps & {
  onClick: () => void;
  toggleSearch: () => void;
};
const NavLinks: React.FC<NavLinkProps> = ({ toggleSearch, ...props }) => {
  return (
    <Flex alignItems="center" {...props}>
      <SearchIcon onClick={toggleSearch}>
        <FaSearch />
      </SearchIcon>
      <Button as="a" href="https://www.classdojo.com/jobs/" target="_blank">
        We're hiring!
      </Button>
    </Flex>
  );
};

const AnimatedNavLinks = posed.div({
  enter: { height: "auto" },
  exit: { height: 0 },
  transition: { default: { ease: "tween", duration: 1000 } },
});

const AnimatedSearch = posed.div({
  enter: { height: "auto" },
  exit: { height: 0 },
  transition: { default: { ease: "tween", duration: 1000 } },
});

const DevBlogHeader: React.FC = () => {
  const data = useStaticQuery(graphql`
    query {
      logo: file(name: { eq: "classdojo-logo-round-120x120" }) {
        childImageSharp {
          fluid(maxWidth: 60) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
    }
  `);

  const { logo } = data;

  const [headerOpen, setHeaderOpen] = useState<boolean>(false);
  const toggleHeader = () => setHeaderOpen(!headerOpen);

  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const toggleSearch = () => setSearchOpen(!searchOpen);

  return (
    <Nav role="navigation">
      <Box height="88px" css={bottomBorder}>
        <Container height="100%">
          <Flex flexDirection="row" alignItems="center" height="100%">
            <Flex alignItems="center" width="100%">
              <Link to={"/"}>
                <Flex alignItems="center">
                  <Logo fluid={logo.childImageSharp.fluid} loading="eager" />
                  <SiteName>ClassDojo Engineering</SiteName>
                </Flex>
              </Link>
              <BurgerButton ml="auto" display={[null, "none"]} onClick={toggleHeader} backgroundVisible={headerOpen} />
            </Flex>
            <NavLinks display={["none", "flex"]} toggleSearch={toggleSearch} onClick={() => setHeaderOpen(false)} />
          </Flex>
        </Container>
      </Box>
      <Transition>
        {headerOpen && (
          <AnimatedNavLinks
            key="navlinks"
            css={css`
              overflow: hidden;
              position: absolute;
              width: 100%;
              background: white;
              z-index: 100;
            `}
          >
            <NavLinks
              flexDirection="column"
              display={["flex", "flex", "none"]}
              py={3}
              css={bottomBorder}
              width="100%"
              toggleSearch={toggleSearch}
              onClick={() => setHeaderOpen(false)}
            />
          </AnimatedNavLinks>
        )}
      </Transition>
      <Transition>
        {searchOpen && (
          <AnimatedSearch
            key="search"
            css={css`
              overflow: hidden;
              position: absolute;
              width: 100%;
              background: white;
              z-index: 100;
            `}
          >
            <Flex
              width={"100%"}
              minHeight="65px"
              backgroundColor={"#f5f5f5"}
              alignItems="center"
              justifyContent="center"
            >
              <BlogSearchForm closeSearch={() => setSearchOpen(false)} />
            </Flex>
          </AnimatedSearch>
        )}
      </Transition>
    </Nav>
  );
};

export default DevBlogHeader;
